"use client";
import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebase";

export default function CouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  const [couponName, setCouponName] = useState("");
  const [editingId, setEditingId] = useState(null);

  // 🔥 Generate Random Code
  const generateCouponCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  // 📥 Fetch Coupons
  const fetchCoupons = async () => {
    try {
      const q = query(
        collection(db, "coupons"),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(q);

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setCoupons(data);
    } catch (error) {
      console.error("Error fetching coupons:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  // ➕ Add Coupon
  const handleAddCoupon = async () => {
    if (!couponName.trim()) {
      alert("Enter coupon name");
      return;
    }

    const code = generateCouponCode();

    try {
      await addDoc(collection(db, "coupons"), {
        name: couponName,
        code,
        createdAt: serverTimestamp(),
      });

      setCouponName("");
      fetchCoupons();
    } catch (error) {
      console.error("Error adding coupon:", error);
    }
  };

  // ✏️ Edit Coupon
  const handleEditCoupon = async (id) => {
    if (!couponName.trim()) {
      alert("Enter coupon name");
      return;
    }

    try {
      await updateDoc(doc(db, "coupons", id), {
        name: couponName,
      });

      setEditingId(null);
      setCouponName("");
      fetchCoupons();
    } catch (error) {
      console.error("Error updating coupon:", error);
    }
  };

  // ❌ Delete Coupon
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Delete this coupon?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "coupons", id));
      fetchCoupons();
    } catch (error) {
      console.error("Error deleting coupon:", error);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto shadow-lg rounded-2xl p-6">

        {/* 🔷 Heading */}
        <h2 className="text-2xl font-bold mb-6 text-gray-800">
          Coupon Management
        </h2>

        {/* ➕ ADD / EDIT SECTION */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <input
            type="text"
            placeholder="Enter Coupon Name (e.g. Naveen Referral)"
            value={couponName}
            onChange={(e) => setCouponName(e.target.value)}
            className="border p-2 rounded w-80 text-black"
          />

          {editingId ? (
            <button
              onClick={() => handleEditCoupon(editingId)}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Update Coupon
            </button>
          ) : (
            <button
              onClick={handleAddCoupon}
              className="bg-orange-500 text-white px-4 py-2 rounded"
            >
              Add Coupon
            </button>
          )}
        </div>

        {/* ⏳ Loading */}
        {loading ? (
          <p className="text-center text-gray-500 py-10">
            Loading coupons...
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-xl overflow-hidden">

              {/* 🧠 Head */}
              <thead className="bg-gray-100 text-gray-600 text-sm uppercase">
                <tr>
                  <th className="px-4 py-3 border">Name</th>
                  <th className="px-4 py-3 border">Code</th>
                  <th className="px-4 py-3 border">Date</th>
                  <th className="px-4 py-3 border">Actions</th>
                </tr>
              </thead>

              {/* 📊 Body */}
              <tbody>
                {coupons.length === 0 ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-center py-6 text-gray-500"
                    >
                      No coupons found
                    </td>
                  </tr>
                ) : (
                  coupons.map((c) => (
                    <tr
                      key={c.id}
                      className="hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-3 border font-medium text-gray-800">
                        {c.name}
                      </td>

                      <td className="px-4 py-3 border text-blue-600 font-mono">
                        {c.code}
                      </td>

                      <td className="px-4 py-3 border text-sm text-gray-500">
                        {c.createdAt
                          ? new Date(
                            c.createdAt.seconds * 1000
                          ).toLocaleString()
                          : ""}
                      </td>

                      <td className="px-4 py-3 border flex gap-2 justify-center">

                        {/* ✏️ EDIT */}
                        <button
                          onClick={() => {
                            setCouponName(c.name);
                            setEditingId(c.id);
                          }}
                          className="bg-blue-500 text-white px-3 py-1 rounded text-sm"
                        >
                          Edit
                        </button>

                        {/* ❌ DELETE */}
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="bg-red-500 text-white px-3 py-1 rounded text-sm"
                        >
                          Delete
                        </button>

                      </td>
                    </tr>
                  ))
                )}
              </tbody>

            </table>
          </div>
        )}
      </div>
    </div>
  );
}